import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EventIcon from '@mui/icons-material/Event';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Button,
  FormControlLabel
} from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPlayerHealth } from 'API/v1/admin/GetPlayerHealth';
import {
  eventLabel,
  eventSource,
  groupEventsBySession,
  latestCursor,
  mergeEvents
} from 'utils/playerHealthTimeline';

const DEFAULT_LIMIT = 500;
const LIVE_INTERVAL_MS = 1000;

function defaultSinceIso() {
  return new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString();
}

function clampLimit(value) {
  const parsed = parseInt(value || String(DEFAULT_LIMIT), 10);
  if (Number.isNaN(parsed)) return DEFAULT_LIMIT;
  return Math.max(1, Math.min(5000, parsed));
}

function toLocalDateTimeInput(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
}

function fromLocalDateTimeInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
}

function fmtTime(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString();
  } catch (_) {
    return ts;
  }
}

function fmtClock(date) {
  if (!date) return '-';
  return date.toLocaleTimeString();
}

function durationSeconds(startedAt, lastEventAt) {
  if (!startedAt || !lastEventAt) return null;
  const a = new Date(startedAt).getTime();
  const b = new Date(lastEventAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  const secs = Math.max(0, Math.round((b - a) / 1000));
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function sourceColor(source) {
  if (source === 'frontend') return 'secondary';
  if (source === 'websocket') return 'info';
  if (source === 'relay') return 'success';
  return 'primary';
}

function levelColor(level) {
  if (level === 'error') return 'error';
  if (level === 'warning' || level === 'warn') return 'warning';
  return 'default';
}

function EventRow({ event }) {
  const meta = eventLabel(event.event || '');
  const source = eventSource(event);
  const level = String(event.level || 'info');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="caption" sx={{ minWidth: 150, color: 'text.secondary', fontFamily: 'monospace' }}>
        {fmtTime(event.ts)}
      </Typography>
      <Chip size="small" label={source} color={sourceColor(source)} variant="outlined" />
      <Chip size="small" label={level} color={levelColor(level)} variant="outlined" />
      <Chip
        size="small"
        label={meta.label}
        color={meta.color}
        variant="outlined"
        sx={{ minWidth: 190, maxWidth: 280, justifyContent: 'flex-start' }}
      />
      {event.ch && <Chip size="small" label={event.ch} variant="outlined" sx={{ maxWidth: 220 }} />}
      {event.pp && (
        <Typography variant="caption" color="text.secondary">
          {event.pp}
        </Typography>
      )}
      {event.attrs && Object.keys(event.attrs).length > 0 && (
        <Tooltip title={JSON.stringify(event.attrs, null, 2)} placement="left">
          <Chip size="small" label="attrs" variant="outlined" sx={{ ml: 'auto' }} />
        </Tooltip>
      )}
    </Box>
  );
}

EventRow.propTypes = {
  event: PropTypes.shape({
    event: PropTypes.string,
    ts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    svc: PropTypes.string,
    level: PropTypes.string,
    ch: PropTypes.string,
    pp: PropTypes.string,
    attrs: PropTypes.object
  }).isRequired
};

function SessionCard({ session }) {
  const dur = durationSeconds(session.startedAt, session.lastEventAt);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" label={session.publicPath || 'sin usuario'} color="primary" variant="outlined" />
          {session.channelName && <Chip size="small" label={session.channelName} variant="outlined" />}
          {session.diagnostics.map((diag) => (
            <Chip key={diag.key} size="small" label={diag.label} color={diag.color} />
          ))}
          <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: '100%', overflowWrap: 'anywhere' }} color="text.secondary">
            {session.viewId || '(sin viewId)'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {fmtTime(session.startedAt)} - {fmtTime(session.lastEventAt)} {dur && `(${dur})`}
          </Typography>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <Box>
          {session.events.map((event, i) => (
            <EventRow key={(event._id || event.ts || '') + '-' + i} event={event} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

SessionCard.propTypes = {
  session: PropTypes.shape({
    viewId: PropTypes.string,
    publicPath: PropTypes.string,
    channelName: PropTypes.string,
    startedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lastEventAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    diagnostics: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired
    })).isRequired,
    events: PropTypes.array.isRequired
  }).isRequired
};

function EmptyState() {
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography color="text.secondary">No hay eventos que coincidan con los filtros actuales.</Typography>
    </Box>
  );
}

function PlayerHealthTimeline() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialSince] = useState(defaultSinceIso);
  const abortRef = useRef(null);
  const cursorRef = useRef(null);

  const publicPath = searchParams.get('publicPath') || '';
  const viewId = searchParams.get('viewId') || '';
  const event = searchParams.get('event') || '';
  const source = searchParams.get('source') || '';
  const level = searchParams.get('level') || '';
  const channelName = searchParams.get('channelName') || '';
  const since = searchParams.get('since') || initialSince;
  const until = searchParams.get('until') || '';
  const limit = clampLimit(searchParams.get('limit'));
  const view = searchParams.get('groupBy') === 'flat' ? 'flat' : 'session';
  const liveParam = searchParams.get('live');
  const live = liveParam === null ? !until : liveParam === '1';

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [meta, setMeta] = useState(null);

  // NOTE: We deliberately avoid `setSearchParams()` here. In react-router-dom@6.11
  // the bundled `createBrowserRouter` always forces `v7_prependBasename: true`,
  // and `setSearchParams` calls `navigate("?" + params)` which is treated as a
  // relative path. With basename="/frontend" that turns into
  // `/frontend/frontend/...` on every change and breaks the SPA.
  // Passing an explicit pathname (already stripped of the basename by
  // `useLocation`) lets the router prepend the basename exactly once.
  const updateFilters = useCallback((updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    const searchString = next.toString();
    navigate(
      {
        pathname: location.pathname || '/',
        search: searchString ? `?${searchString}` : '',
        hash: ''
      },
      { replace: true, preventScrollReset: true }
    );
  }, [searchParams, location.pathname, navigate]);

  const fetchData = useCallback(async ({ incremental = false } = {}) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    if (incremental) {
      setRefreshing(true);
    } else {
      setLoading(true);
      setEvents([]);
      cursorRef.current = null;
    }
    setError(null);

    try {
      const effectiveSince = incremental && cursorRef.current ? cursorRef.current : since;
      const res = await getPlayerHealth({
        publicPath: publicPath.trim() || undefined,
        viewId: viewId.trim() || undefined,
        event: event.trim() || undefined,
        source: source || undefined,
        level: level || undefined,
        channelName: channelName.trim() || undefined,
        since: effectiveSince || undefined,
        until: live ? undefined : until || undefined,
        limit,
        groupBySession: false,
        signal: controller.signal
      });

      if (controller.signal.aborted) return;
      if (!res) {
        setError('Sin respuesta del backend.');
        return;
      }

      const incoming = Array.isArray(res.events) ? res.events : [];
      setEvents((current) => mergeEvents(incremental ? current : [], incoming, limit));
      const incomingCursor = res.cursorTs || latestCursor(incoming);
      if (incomingCursor && (!cursorRef.current || incomingCursor > cursorRef.current)) {
        cursorRef.current = incomingCursor;
      }
      setMeta(res.meta || null);
      setLastUpdatedAt(new Date());
    } catch (e) {
      if (!controller.signal.aborted) {
        setError(String(e && e.message ? e.message : e));
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [publicPath, viewId, event, source, level, channelName, since, until, live, limit]);

  useEffect(() => {
    fetchData({ incremental: false });
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchData]);

  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      fetchData({ incremental: true });
    }, LIVE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [live, fetchData]);

  const sessions = useMemo(() => groupEventsBySession(events), [events]);

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <ViewTimelineIcon color="primary" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Players B/C - Health Timeline</Typography>
          <Typography variant="body2" color="text.secondary">
            Eventos de reproducción agrupados por sesión
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <FormControlLabel
          control={(
            <Switch
              checked={live}
              onChange={(e) => updateFilters({ live: e.target.checked ? '1' : '0', until: e.target.checked ? '' : until })}
            />
          )}
          label="En vivo"
        />
        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, v) => v && updateFilters({ groupBy: v })}
        >
          <ToggleButton value="session">Por sesion</ToggleButton>
          <ToggleButton value="flat">Lista plana</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => fetchData({ incremental: false })} disabled={loading}>
          Refrescar
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField label="publicPath" size="small" fullWidth value={publicPath} onChange={(e) => updateFilters({ publicPath: e.target.value })} placeholder="ej. alice" />
        <TextField label="viewId" size="small" fullWidth value={viewId} onChange={(e) => updateFilters({ viewId: e.target.value })} placeholder="ej. b_65f7...-player-alice" />
        <TextField label="canal" size="small" fullWidth value={channelName} onChange={(e) => updateFilters({ channelName: e.target.value })} placeholder="ej. CNN HD" />
        <TextField label="evento" size="small" fullWidth value={event} onChange={(e) => updateFilters({ event: e.target.value })} placeholder="ej. limitEntered" />
      </Stack>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="player-health-source-label">origen</InputLabel>
          <Select
            labelId="player-health-source-label"
            label="origen"
            value={source}
            onChange={(e) => updateFilters({ source: e.target.value })}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="backend">Backend</MenuItem>
            <MenuItem value="frontend">Frontend</MenuItem>
            <MenuItem value="websocket">Websocket</MenuItem>
            <MenuItem value="relay">Relay Play C</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="player-health-level-label">nivel</InputLabel>
          <Select
            labelId="player-health-level-label"
            label="nivel"
            value={level}
            onChange={(e) => updateFilters({ level: e.target.value })}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="desde"
          size="small"
          type="datetime-local"
          fullWidth
          value={toLocalDateTimeInput(since)}
          onChange={(e) => updateFilters({ since: fromLocalDateTimeInput(e.target.value) })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="hasta"
          size="small"
          type="datetime-local"
          fullWidth
          disabled={live}
          value={toLocalDateTimeInput(until)}
          onChange={(e) => updateFilters({ until: fromLocalDateTimeInput(e.target.value), live: '0' })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="limite"
          size="small"
          type="number"
          value={limit}
          onChange={(e) => updateFilters({ limit: clampLimit(e.target.value) })}
          sx={{ maxWidth: 120 }}
        />
        <Button variant="outlined" onClick={() => updateFilters({ since: defaultSinceIso(), until: '', live: '1' })} sx={{ minWidth: 130 }}>
          Ultimas 4h
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          size="small"
          icon={<PlayCircleOutlineIcon />}
          label={live ? 'Live activo' : 'Live pausado'}
          color={live ? 'success' : 'default'}
          variant={live ? 'filled' : 'outlined'}
        />
        {refreshing && <CircularProgress size={16} />}
        <Typography variant="body2" color="text.secondary">
          {view === 'session' ? `${sessions.length} sesiones, ${events.length} eventos` : `${events.length} eventos`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ultima actualizacion: {fmtClock(lastUpdatedAt)}
        </Typography>
        {meta && (
          <Typography variant="body2" color={meta.scanCapReached ? 'warning.main' : 'text.secondary'}>
            Redis scan: {meta.scanned}{meta.scanCapReached ? ' (cap)' : ''}
          </Typography>
        )}
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 4, justifyContent: 'center' }}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Cargando eventos...</Typography>
        </Box>
      )}

      {error && <Typography color="error" sx={{ py: 2 }}>{error}</Typography>}

      {!loading && !error && (
        view === 'session'
          ? (sessions.length === 0 ? <EmptyState /> : sessions.map((session, i) => <SessionCard key={(session.viewId || 'noid') + '-' + i} session={session} />))
          : (events.length === 0 ? <EmptyState /> : <Box>{events.map((evt, i) => <EventRow key={(evt._id || evt.ts || '') + '-' + i} event={evt} />)}</Box>)
      )}
    </Paper>
  );
}

export default PlayerHealthTimeline;
